import type { CSSObjectWithLabel } from 'react-select'
import { PlatformDataType } from 'src/interfaces/Platform.interface'
import { getConnectedPlatformLabel } from '../../utils/connectedPlatformLabel'

export type ListingProfileViewModePayload = {
  mode: 'list' | 'create' | 'edit'
  platformId?: number
}

/** Bump per-platform list refresh counter (Saved Listing Profiles GET). */
export const bumpListingProfileListRefresh = (
  prev: Record<number, number>,
  platformId: number
): Record<number, number> => ({
  ...prev,
  [platformId]: (prev[platformId] ?? 0) + 1,
})

export const resolveListingProfileListPlatformId = (
  formTargetPlatformId: number | null | undefined,
  tabPlatformId: number | undefined
): number | undefined => formTargetPlatformId ?? tabPlatformId ?? undefined

export const resolvePlatformById = (
  platformId: number | null | undefined,
  platforms: PlatformDataType[],
  fallback: PlatformDataType | null
): PlatformDataType | null => {
  if (platformId == null) return fallback
  return (
    platforms.find((p) => p.id != null && Number(p.id) === Number(platformId)) ?? fallback
  )
}

/** Match list row to connected platform for logo/label (id first, then display name). */
export const resolvePlatformForListingProfileRow = (
  platformId: number | null | undefined,
  platformName: string | undefined,
  platforms: PlatformDataType[]
): PlatformDataType | null => {
  const byId = resolvePlatformById(platformId ?? null, platforms, null)
  if (byId) return byId
  const normalized = String(platformName ?? '').trim().toLowerCase()
  if (!normalized) return null
  return (
    platforms.find((p) => {
      const label = getConnectedPlatformLabel(p).toLowerCase()
      return (
        label === normalized ||
        String(p.display_name ?? '').trim().toLowerCase() === normalized ||
        String(p.name ?? '').trim().toLowerCase() === normalized
      )
    }) ?? null
  )
}

export const isListingProfileFormDirty = (input: {
  ruleName: string
  titleInput: string
  editorData: string
  hydratedProductRecipes: { checkValue?: string }[]
}): boolean => {
  if (input.ruleName.trim()) return true
  if (input.titleInput.trim()) return true
  const plainDescription = input.editorData.replace(/<[^>]*>/g, '').trim()
  if (plainDescription) return true
  const recipes = input.hydratedProductRecipes
  if (recipes.length > 1) return true
  if (String(recipes[0]?.checkValue ?? '').trim()) return true
  return false
}

/** Match Bootstrap modal layer (`ListingProfileTargetChannelSelect`). */
export const LISTING_PROFILE_REACT_SELECT_MENU_Z_INDEX = 1060

export function getListingProfileReactSelectMenuPortalTarget(): HTMLElement | undefined {
  return typeof document !== 'undefined' ? document.body : undefined
}

export const listingProfileReactSelectMenuPortalStyles = {
  menuPortal: (base: CSSObjectWithLabel) => ({
    ...base,
    zIndex: LISTING_PROFILE_REACT_SELECT_MENU_Z_INDEX,
  }),
}

/** Match `.product-listing__more-mappings-grid` `--lp-field-*` tokens (control height from SCSS). */
export const LISTING_PROFILE_FORM_FIELD_FONT_SIZE = '0.875rem'
export const LISTING_PROFILE_FORM_FIELD_LINE_HEIGHT = '1.25'

export const listingProfileFormSelectTypographyStyles = {
  placeholder: (base: CSSObjectWithLabel) => ({
    ...base,
    fontSize: LISTING_PROFILE_FORM_FIELD_FONT_SIZE,
    lineHeight: LISTING_PROFILE_FORM_FIELD_LINE_HEIGHT,
  }),
  singleValue: (base: CSSObjectWithLabel) => ({
    ...base,
    color: '#181c32',
    fontSize: LISTING_PROFILE_FORM_FIELD_FONT_SIZE,
    lineHeight: LISTING_PROFILE_FORM_FIELD_LINE_HEIGHT,
  }),
  input: (base: CSSObjectWithLabel) => ({
    ...base,
    margin: '0px',
    padding: '0px',
    fontSize: LISTING_PROFILE_FORM_FIELD_FONT_SIZE,
    lineHeight: LISTING_PROFILE_FORM_FIELD_LINE_HEIGHT,
  }),
}

export const listingProfileFormSelectIndicatorStyles = {
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base: CSSObjectWithLabel) => ({
    ...base,
    color: '#212529',
    padding: '4px',
  }),
  clearIndicator: (base: CSSObjectWithLabel) => ({
    ...base,
    padding: '4px',
    color: '#212529',
    '&:hover': { color: '#212529' },
  }),
}

/** Listing profile misc grid: typography + menu portal; sizing via SCSS on `listing-profile-form-select`. */
export const listingProfileMoreMappingSelectStyles = {
  ...listingProfileFormSelectTypographyStyles,
  ...listingProfileFormSelectIndicatorStyles,
  ...listingProfileReactSelectMenuPortalStyles,
}

/** Normalize GET /api/platform-fields payload (array or paginated `{ results }`). */
export const normalizePlatformFieldList = <T>(payload: unknown): T[] => {
  if (Array.isArray(payload)) return payload as T[]
  if (payload && typeof payload === 'object' && Array.isArray((payload as { results?: unknown }).results)) {
    return (payload as { results: T[] }).results
  }
  return []
}
